import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { qaApi } from '../api/apiClient';
import './AgendaPage.css';

function formatDue(value) {
  if (!value) return 'No due date';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function dueClass(value) {
  if (!value) return '';
  const now = Date.now();
  const due = new Date(value).getTime();
  if (Number.isNaN(due)) return '';
  const diff = due - now;
  if (diff < 0) return 'is-overdue';
  if (diff <= 24 * 60 * 60 * 1000) return 'is-soon';
  return '';
}

export default function AgendaPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await qaApi.listWorkspaceAgendaItems(workspaceId);
        if (!cancelled) setItems(res.data || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const openThread = (item) => {
    const q = item?.questionId;
    if (q != null) {
      navigate(`/workspaces/${workspaceId}/qa?questionId=${q}`);
    } else {
      navigate(`/workspaces/${workspaceId}/qa`);
    }
  };

  const { overdue, soon, later } = useMemo(() => {
    const o = [];
    const s = [];
    const l = [];
    const now = Date.now();
    items.forEach((it) => {
      const due = it?.dueAt ? new Date(it.dueAt).getTime() : NaN;
      if (Number.isNaN(due)) {
        l.push(it);
      } else if (due < now) {
        o.push(it);
      } else if (due - now <= 24 * 60 * 60 * 1000) {
        s.push(it);
      } else {
        l.push(it);
      }
    });
    return { overdue: o, soon: s, later: l };
  }, [items]);

  const renderList = (list) => (
    <div className="v-agenda-list">
      {list.map((item) => (
        <div
          key={item.id}
          className={`v-agenda-card ${dueClass(item.dueAt)}`}
          role="button"
          tabIndex={0}
          onClick={() => openThread(item)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openThread(item);
            }
          }}
        >
          <div className="v-agenda-card__top">
            <h3>{item.title}</h3>
            <span className="v-badge v-badge--warning">{item.status || 'OPEN'}</span>
          </div>
          {item.objective ? <p className="v-agenda-card__objective">{item.objective}</p> : null}
          <div className="v-agenda-card__meta">
            <span>Due: {formatDue(item.dueAt)}</span>
            {item.ownerUserName ? <span>Owner: {item.ownerUserName}</span> : null}
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) return <div className="v-loading">Loading agenda...</div>;

  return (
    <div className="v-page">
      <div className="v-breadcrumb">
        <button onClick={() => navigate('/workspaces')}>Workspaces</button>
        <span>/</span>
        <button type="button" onClick={() => navigate(`/workspaces/${workspaceId}`)}>Workspace</button>
        <span>/</span>
        <span>Agenda</span>
      </div>
      <div className="v-page__header">
        <div>
          <h1 className="v-page__title">Agenda items</h1>
          <p className="v-page__subtitle">All follow-up items created from Q&A threads.</p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="v-empty">
          <h3>No agenda items yet</h3>
          <p>Create agenda items from Q&A threads to plan next actions.</p>
        </div>
      ) : (
        <div className="v-agenda-sections">
          {overdue.length > 0 && (
            <section>
              <h2 className="v-agenda-section-title">Overdue</h2>
              {renderList(overdue)}
            </section>
          )}
          {soon.length > 0 && (
            <section>
              <h2 className="v-agenda-section-title">Due within 24 hours</h2>
              {renderList(soon)}
            </section>
          )}
          {later.length > 0 && (
            <section>
              <h2 className="v-agenda-section-title">Later</h2>
              {renderList(later)}
            </section>
          )}
        </div>
      )}
    </div>
  );
}
